// src/pages/InstructorDashboard.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { db } from "../firebase";
import {
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
  limit as qLimit,
  doc,
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  ArrowRight,
  TrendingUp,
  DollarSign,
  Clock,
  Users,
  Layers,
  Filter,
} from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip as ReTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

/* -------------------- tiny utils -------------------- */
function pickInstructorShare(o) {
  const fields = [
    "instructorShare",
    "instructor_share",
    "instructorShareLKR",
    "instructorShareUSD",
    "instructorAmount",
    "instructor_amount",
    "credit",
    "creditAmount",
    "creditLKR",
  ];
  for (const f of fields) {
    const n = Number(o?.[f]);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return NaN; // no fallback to total/price
}
const money = (n) => `LKR ${Number(n || 0).toLocaleString("en-LK")}`;
const COLORS = ["#7C3AED", "#22C55E", "#F59E0B", "#EF4444", "#06B6D4"];

function getItemPrice(it) {
  const n = Number(it?.price ?? it?.basePrice ?? it?.amount ?? 0);
  return Number.isFinite(n) ? n : 0;
}
function formatLKR(n) {
  if (!Number.isFinite(n) || n <= 0) return "Free";
  return `LKR ${n.toLocaleString("en-LK", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
}
const pickTs = (o) =>
  o.approvedAt?.toDate?.() ||
  o.approvedAt ||
  o.submittedAt?.toDate?.() ||
  o.createdAt?.toDate?.() ||
  (o.createdAt ? new Date(o.createdAt) : new Date());

/* ---- Smart thumbnail (Drive + YouTube fallback) ---- */
function extractDriveId(rawUrl = "") {
  const url = String(rawUrl || "").trim();
  if (!url) return null;
  try {
    const u = new URL(url);
    if (!u.hostname.includes("drive.google.com")) return null;
    const m = url.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
    if (m && m[1]) return m[1];
    const q = u.searchParams.get("id");
    if (q) return q;
  } catch (_) {}
  return null;
}
function buildDriveThumbs(thumbnailUrl) {
  const id = extractDriveId(thumbnailUrl);
  if (!id) return [];
  return [
    `https://drive.usercontent.google.com/uc?id=${id}&export=view`,
    `https://drive.google.com/uc?export=view&id=${id}`,
    `https://lh3.googleusercontent.com/d/${id}=w1200-h800-no`,
    `https://drive.google.com/thumbnail?id=${id}&sz=w1200`,
  ];
}
function getYouTubeThumbnail(url) {
  const m = String(url || "").match(
    /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/))([\w-]{11})/
  );
  return m ? `https://img.youtube.com/vi/${m[1]}/hqdefault.jpg` : null;
}
function SmartThumb({
  item,
  className = "w-12 h-12 rounded-lg object-cover border",
}) {
  const yt =
    Array.isArray(item?.youtubeLinks) && item.youtubeLinks[0]
      ? item.youtubeLinks[0].thumbnail ||
        getYouTubeThumbnail(item.youtubeLinks[0].url)
      : null;
  const candidates = [];
  const primary = item?.thumbnailUrl || item?.thumbnail;
  if (primary) candidates.push(...buildDriveThumbs(primary));
  if (yt) candidates.push(yt);
  candidates.push("https://placehold.co/120x120?text=—");
  const [idx, setIdx] = useState(0);
  const src = candidates[Math.min(idx, candidates.length - 1)];
  return (
    <img
      src={src}
      alt={item?.title || "thumb"}
      className={className}
      referrerPolicy="no-referrer"
      onError={() => idx < candidates.length - 1 && setIdx((n) => n + 1)}
      draggable="false"
    />
  );
}

/* -------------------- page -------------------- */
export default function InstructorDashboard() {
  const auth = getAuth();
  const [authUser, setAuthUser] = useState(null);
  const [profile, setProfile] = useState(null);

  const [payouts, setPayouts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [myItems, setMyItems] = useState([]);

  // base price lookup for fallback share
  const priceById = useMemo(() => {
    const m = new Map();
    myItems.forEach((x) => m.set(x.id, getItemPrice(x)));
    return m;
  }, [myItems]);

  // Tabs
  const [activeTab, setActiveTab] = useState(null); // 'course' | 'product'
  const decidedInitial = useRef(false);

  // FILTER STATE (tab-aware)
  const [preset, setPreset] = useState("7d"); // '7d' | '30d' | '90d' | 'custom'
  const [dateFrom, setDateFrom] = useState(getDateNDaysAgo(7));
  const [dateTo, setDateTo] = useState(toISODate(new Date()));
  const [metric, setMetric] = useState("revenue"); // 'revenue' | 'sales'
  const [group, setGroup] = useState("day"); // 'day' | 'week' | 'month'
  const [statusSet, setStatusSet] = useState(
    new Set(["paid", "completed", "approved", "success"])
  );
  const [itemIds, setItemIds] = useState(new Set()); // selected item IDs
  const [itemSearch, setItemSearch] = useState("");

  const orderUnsubsRef = useRef([]);

  /* ---------- auth + profile ---------- */
  useEffect(
    () => onAuthStateChanged(auth, (u) => setAuthUser(u || null)),
    [auth]
  );
  useEffect(() => {
    if (!authUser?.uid) return;
    return onSnapshot(doc(db, "users", authUser.uid), (snap) =>
      setProfile(snap.exists() ? snap.data() : null)
    );
  }, [authUser?.uid]);

  /* ---------- my listings ---------- */
  useEffect(() => {
    if (!authUser?.uid) return;
    const qItems = query(
      collection(db, "courses"),
      where("createdBy", "==", authUser.uid)
    );
    return onSnapshot(qItems, (snap) => {
      const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setMyItems(docs);
    });
  }, [authUser?.uid]);

  // decide default tab once we know counts
  useEffect(() => {
    if (decidedInitial.current) return;
    if (activeTab) return;
    const courseCount = myItems.filter(
      (x) => (x.type || "").toLowerCase() === "course"
    ).length;
    const productCount = myItems.filter(
      (x) => (x.type || "").toLowerCase() === "product"
    ).length;
    if (courseCount + productCount === 0) return;
    setActiveTab(productCount > courseCount ? "product" : "course");
    decidedInitial.current = true;
  }, [myItems, activeTab]);

  // when switching tabs, clear item selections (tab-scoped)
  useEffect(() => {
    setItemIds(new Set());
    setItemSearch("");
  }, [activeTab]);

  /* ---------- payouts ---------- */
  useEffect(() => {
    if (!authUser?.uid) return;
    const qP = query(
      collection(db, "payouts"),
      where("userId", "==", authUser.uid)
    );
    return onSnapshot(qP, (snap) =>
      setPayouts(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
  }, [authUser?.uid]);

  /* ---------- orders (primary + fallback) ---------- */
  useEffect(() => {
    orderUnsubsRef.current.forEach((u) => u && u());
    orderUnsubsRef.current = [];
    if (!authUser?.uid) return;

    // PRIMARY: instructorId
    const primaryQ = query(
      collection(db, "orders"),
      where("instructorId", "==", authUser.uid),
      orderBy("createdAt", "desc"),
      qLimit(600)
    );
    const primaryUnsub = onSnapshot(primaryQ, (snap) => {
      setOrders(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    orderUnsubsRef.current.push(primaryUnsub);

    return () => {
      orderUnsubsRef.current.forEach((u) => u && u());
      orderUnsubsRef.current = [];
    };
  }, [authUser?.uid]);

  // FALLBACK: by courseId IN [...]
  useEffect(() => {
    if (!authUser?.uid) return;
    if (orders.length > 0) return;
    if (myItems.length === 0) return;

    const ids = myItems.map((c) => c.id);
    const chunks = [];
    for (let i = 0; i < ids.length; i += 10) chunks.push(ids.slice(i, i + 10));

    const localMap = new Map();
    const unsubs = chunks.map((chunk) => {
      const qO = query(
        collection(db, "orders"),
        where("courseId", "in", chunk),
        qLimit(400)
      );
      return onSnapshot(qO, (snap) => {
        snap.forEach((d) => localMap.set(d.id, { id: d.id, ...d.data() }));
        const merged = Array.from(localMap.values()).sort((a, b) => {
          const at = pickTs(a);
          const bt = pickTs(b);
          return bt - at;
        });
        setOrders(merged);
      });
    });

    orderUnsubsRef.current.push(...unsubs);
    return () => unsubs.forEach((u) => u && u());
  }, [authUser?.uid, myItems, orders.length]);

  /* ---------- derived: active type + items + normalized ---------- */
  const activeType = activeTab || "course";

  const itemsForTab = useMemo(
    () => myItems.filter((x) => (x.type || "").toLowerCase() === activeType),
    [myItems, activeType]
  );

  const typeById = useMemo(() => {
    const m = new Map();
    myItems.forEach((x) => m.set(x.id, (x.type || "course").toLowerCase()));
    return m;
  }, [myItems]);

  const normalized = useMemo(() => {
    return orders.map((o) => {
      const statusNorm = String(o.status || "").toLowerCase();
      const ts = pickTs(o);
      const key = o.courseId || o.itemId || "";
      const title =
        o.itemTitle || o.courseTitle || o.itemId || o.courseId || "Unknown";
      let type = (o.type || o.itemType || "").toLowerCase();
      if (!type) type = typeById.get(key) || "course";
      const amount = Number(o.amount || o.total || o.price || 0);
      let share = pickInstructorShare(o);
      if (!Number.isFinite(share) || share <= 0) {
        const fallback = priceById.get(key);
        share = Number.isFinite(fallback) ? fallback : 0;
      }
      return { ...o, statusNorm, ts, key, title, type, amount, share };
    });
  }, [orders, typeById, priceById]);

  /* ---------- item options + select-all ---------- */
  const itemOptions = useMemo(() => {
    const fromMy = itemsForTab.map((x) => ({
      id: x.id,
      title: x.title || "(Untitled)",
      type: (x.type || "course").toLowerCase(),
    }));
    const extra = normalized
      .filter((o) => o.type === activeType)
      .map((o) => ({ id: o.key || o.title, title: o.title, type: o.type }))
      .filter((x) => x.id && !fromMy.find((y) => y.id === x.id));
    const merged = [...fromMy, ...extra];
    const q = itemSearch.trim().toLowerCase();
    return q ? merged.filter((i) => i.title.toLowerCase().includes(q)) : merged;
  }, [itemsForTab, normalized, itemSearch, activeType]);

  const allVisibleIds = useMemo(
    () => itemOptions.map((it) => it.id || it.title).filter(Boolean),
    [itemOptions]
  );
  const allSelected =
    allVisibleIds.length > 0 && allVisibleIds.every((id) => itemIds.has(id));
  const someSelected =
    !allSelected && allVisibleIds.some((id) => itemIds.has(id));

  const selAllRef = useRef(null);
  useEffect(() => {
    if (selAllRef.current) selAllRef.current.indeterminate = someSelected;
  }, [someSelected]);

  /* ---------- all statuses (tab-scoped) ---------- */
  const allStatuses = useMemo(() => {
    const s = new Set(
      normalized
        .filter((o) => o.type === activeType)
        .map((o) => o.statusNorm)
        .filter(Boolean)
    );
    return Array.from(s);
  }, [normalized, activeType]);

  /* ---------- date preset sync ---------- */
  useEffect(() => {
    if (preset === "custom") return;
    const n = preset === "7d" ? 7 : preset === "30d" ? 30 : 90;
    setDateFrom(getDateNDaysAgo(n));
    setDateTo(toISODate(new Date()));
  }, [preset]);

  /* ---------- filtered rows (tab-aware) ---------- */
  const filtered = useMemo(() => {
    const from = new Date(dateFrom);
    from.setHours(0, 0, 0, 0);
    const to = new Date(dateTo);
    to.setHours(23, 59, 59, 999);

    return normalized.filter((o) => {
      if (o.type !== activeType) return false; // tab filter
      if (statusSet.size > 0 && !statusSet.has(o.statusNorm)) return false;
      if (itemIds.size > 0 && !itemIds.has(o.key || o.title)) return false;
      return o.ts >= from && o.ts <= to;
    });
  }, [normalized, activeType, statusSet, itemIds, dateFrom, dateTo]);

  /* ---------- totals & charts from filtered ---------- */
  const totals = useMemo(() => {
    const per = new Map();
    filtered.forEach((o) => {
      const prev = per.get(o.title) || { title: o.title, revenue: 0, count: 0 };
      prev.revenue += o.share;
      prev.count += 1;
      per.set(o.title, prev);
    });
    const topList = Array.from(per.values())
      .sort((a, b) =>
        metric === "revenue" ? b.revenue - a.revenue : b.count - a.count
      )
      .slice(0, 5);

    const buckets = bucketize(filtered, group, metric);
    return { orderCount: filtered.length, topList, series: buckets };
  }, [filtered, group, metric]);

  // KPIs from payouts (global)
  const totalSales = useMemo(
    () =>
      payouts
        .filter((p) =>
          String(p.role || p.source || "")
            .toLowerCase()
            .includes("instructor")
        )
        .reduce((s, p) => s + Number(p.amount || 0), 0),
    [payouts]
  );
  const pendingPayout = useMemo(
    () =>
      payouts
        .filter((p) => String(p.status || "").toLowerCase() === "pending")
        .reduce((s, p) => s + Number(p.amount || 0), 0),
    [payouts]
  );

  const displayName = (
    profile?.fullName ||
    authUser?.displayName ||
    "Instructor"
  ).toLowerCase();

  if (authUser === null)
    return <div className="max-w-7xl mx-auto p-8">Loading…</div>;

  /* -------------------- UI -------------------- */
  return (
    <div className="relative">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -top-24 left-1/2 -translate-x-1/2 h-[340px] w-[1000px] rounded-full bg-gradient-to-r from-fuchsia-400/15 via-indigo-400/15 to-sky-400/15 blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto p-6 md:p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-3xl md:text-4xl font-extrabold tracking-tight">
              Welcome back, <span className="capitalize">{displayName}</span> 👋
            </div>
            <div className="text-gray-500 mt-1">
              Your instructor analytics overview
            </div>
          </div>
          <a
            href="/manage-listing"
            className="inline-flex items-center gap-2 bg-gray-900 text-white px-4 py-2 rounded-xl shadow hover:bg-black"
          >
            Manage Listings <ArrowRight size={18} />
          </a>
        </div>

        {/* TABS */}
        <div className="mb-4">
          <div className="inline-flex rounded-xl border border-gray-200 bg-white p-1 shadow-sm">
            {["course", "product"].map((t) => {
              const count =
                t === "course"
                  ? itemsForTypeCount(myItems, "course")
                  : itemsForTypeCount(myItems, "product");
              const active = activeType === t;
              return (
                <button
                  key={t}
                  onClick={() => {
                    decidedInitial.current = true;
                    setActiveTab(t);
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold capitalize transition ${
                    active
                      ? "bg-indigo-600 text-white"
                      : "text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  {t}{" "}
                  {count > 0 && (
                    <span
                      className={`ml-2 text-[11px] rounded-full px-2 py-0.5 ${
                        active ? "bg-white/20" : "bg-gray-100 text-gray-500"
                      }`}
                    >
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* FILTER BAR */}
        <div className="bg-white/95 border border-gray-100 rounded-2xl shadow p-4 mb-6">
          <div className="flex items-center gap-2 text-sm font-semibold mb-3">
            <Filter size={16} /> Advanced Filters ·{" "}
            <span className="capitalize">{activeType}</span>
          </div>

          <div className="grid lg:grid-cols-5 sm:grid-cols-2 gap-3">
            {/* Date Preset + Custom */}
            <div className="col-span-2">
              <div className="flex items-center gap-2 mb-2">
                {["7d", "30d", "90d", "custom"].map((p) => (
                  <button
                    key={p}
                    onClick={() => setPreset(p)}
                    className={`px-3 py-1.5 rounded-full border text-xs ${
                      preset === p
                        ? "bg-indigo-600 text-white border-indigo-600"
                        : "bg-white hover:bg-gray-50 border-gray-200"
                    }`}
                  >
                    {p === "custom" ? "Custom" : p.toUpperCase()}
                  </button>
                ))}
              </div>
              {preset === "custom" && (
                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="w-full rounded-lg border-gray-200"
                  />
                  <span className="text-gray-400">to</span>
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="w-full rounded-lg border-gray-200"
                  />
                </div>
              )}
            </div>

            {/* Status multiselect */}
            <div>
              <div className="text-xs text-gray-500 mb-1">Status</div>
              <div className="grid grid-cols-2 gap-1">
                {allStatuses.map((s) => (
                  <label
                    key={s}
                    className="flex items-center gap-2 text-xs bg-gray-50 rounded-lg px-2 py-1"
                  >
                    <input
                      type="checkbox"
                      checked={statusSet.has(s)}
                      onChange={(e) => {
                        const next = new Set(statusSet);
                        e.target.checked ? next.add(s) : next.delete(s);
                        setStatusSet(next);
                      }}
                    />
                    <span className="capitalize">{s || "unknown"}</span>
                  </label>
                ))}
                {allStatuses.length === 0 && (
                  <div className="text-xs text-gray-400">No status yet</div>
                )}
              </div>
              <div className="mt-3 text-xs text-gray-500">Metric</div>
              <div className="mt-1 flex gap-2">
                {["revenue", "sales"].map((m) => (
                  <button
                    key={m}
                    onClick={() => setMetric(m)}
                    className={`px-3 py-1.5 rounded-full border text-xs ${
                      metric === m
                        ? "bg-indigo-600 text-white border-indigo-600"
                        : "bg-white hover:bg-gray-50 border-gray-200"
                    }`}
                  >
                    {m === "revenue" ? "Revenue" : "Sales"}
                  </button>
                ))}
              </div>
            </div>

            {/* Items selector (tab-scoped) */}
            <div className="col-span-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="text-xs text-gray-500 mb-1">Items</div>
                  <label className="flex items-center gap-2 text-[11px] text-gray-700">
                    <input
                      type="checkbox"
                      ref={selAllRef}
                      checked={allSelected}
                      onChange={(e) => {
                        const next = new Set(itemIds);
                        if (e.target.checked) {
                          allVisibleIds.forEach((id) => next.add(id));
                        } else {
                          allVisibleIds.forEach((id) => next.delete(id));
                        }
                        setItemIds(next);
                      }}
                    />
                    Select all
                  </label>
                </div>

                <button
                  className="text-[11px] text-indigo-600"
                  onClick={() => setItemIds(new Set())}
                  title="Clear selection"
                >
                  Clear
                </button>
              </div>

              <input
                type="text"
                placeholder={`Search ${activeType}s…`}
                className="w-full rounded-lg border-gray-200 mb-2"
                value={itemSearch}
                onChange={(e) => setItemSearch(e.target.value)}
              />
              <div className="max-h-24 overflow-y-auto rounded-lg border border-gray-200 p-2 bg-white">
                {itemOptions.length === 0 ? (
                  <div className="text-xs text-gray-400">No items</div>
                ) : (
                  itemOptions.slice(0, 40).map((it) => {
                    const id = it.id || it.title;
                    return (
                      <label
                        key={id}
                        className="flex items-center gap-2 text-xs py-0.5"
                      >
                        <input
                          type="checkbox"
                          checked={itemIds.has(id)}
                          onChange={(e) => {
                            const next = new Set(itemIds);
                            e.target.checked ? next.add(id) : next.delete(id);
                            setItemIds(next);
                          }}
                        />
                        <span className="truncate">{it.title}</span>
                        <span className="ml-auto text-[10px] text-gray-400 capitalize">
                          {it.type}
                        </span>
                      </label>
                    );
                  })
                )}
              </div>
            </div>

            {/* Group + Reset + Export */}
            <div className="lg:col-span-1 sm:col-span-2 flex items-end justify-between gap-2">
              <div>
                <div className="text-xs text-gray-500 mb-1">Group by</div>
                <div className="flex gap-2">
                  {["day", "week", "month"].map((g) => (
                    <button
                      key={g}
                      onClick={() => setGroup(g)}
                      className={`px-2.5 py-1 rounded-full border text-xs ${
                        group === g
                          ? "bg-indigo-600 text-white border-indigo-600"
                          : "bg-white hover:bg-gray-50 border-gray-200"
                      }`}
                    >
                      {g}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex gap-2 ml-auto">
                <button
                  className="px-3 py-1.5 rounded-lg border text-xs"
                  onClick={() =>
                    resetFilters(
                      setPreset,
                      setDateFrom,
                      setDateTo,
                      setMetric,
                      setGroup,
                      setStatusSet,
                      setItemIds,
                      setItemSearch
                    )
                  }
                >
                  Reset
                </button>
                <button
                  className="px-3 py-1.5 rounded-lg bg-gray-900 text-white text-xs"
                  onClick={() => exportCSV(filtered)}
                >
                  Export CSV
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <KPI
            icon={<DollarSign />}
            title="Total Sales"
            value={money(totalSales)}
            sub="+ all-time"
          />
          <KPI
            icon={<Clock />}
            title="Pending Payout"
            value={money(pendingPayout)}
            sub="awaiting clearance"
          />
          <KPI
            icon={<Users />}
            title={`Orders (${activeType})`}
            value={totals.orderCount}
            sub="matches filter"
          />
          <KPI
            icon={<TrendingUp />}
            title={metric === "revenue" ? "Trend (sum)" : "Trend (count)"}
            value={
              metric === "revenue"
                ? money(totals.series.reduce((s, d) => s + d.value, 0))
                : totals.series.reduce((s, d) => s + d.value, 0)
            }
            sub={`${group} • ${
              preset === "custom" ? `${dateFrom} → ${dateTo}` : preset
            }`}
          />
        </div>

        {/* Chart + Top */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <div className="col-span-2 bg-white/95 rounded-2xl p-6 shadow border border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <div className="font-semibold capitalize">
                {metric === "revenue" ? "Revenue" : "Sales"} by {group} •{" "}
                {activeType}
              </div>
            </div>
            <div className="h-60">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={totals.series}>
                  <defs>
                    <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366F1" stopOpacity={0.6} />
                      <stop offset="95%" stopColor="#6366F1" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <ReTooltip
                    formatter={(v) => (metric === "revenue" ? money(v) : v)}
                  />
                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke="#6366F1"
                    fill="url(#rev)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white/95 rounded-2xl p-6 shadow border border-gray-100">
            <div className="font-semibold mb-2">Top Performers</div>
            {totals.topList.length === 0 ? (
              <div className="text-gray-400 text-sm">No sales in range.</div>
            ) : (
              <>
                <PieChart width={340} height={220}>
                  <Pie
                    data={totals.topList.map((t) => ({
                      name: t.title,
                      value: metric === "revenue" ? t.revenue : t.count,
                    }))}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                  >
                    {totals.topList.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <ReTooltip
                    formatter={(v) => (metric === "revenue" ? money(v) : v)}
                  />
                </PieChart>
                <ul className="mt-2 space-y-1 text-sm">
                  {totals.topList.map((t, i) => (
                    <li key={i} className="flex items-center justify-between">
                      <span className="truncate">
                        {i + 1}. {t.title}
                      </span>
                      <span className="font-semibold">
                        {metric === "revenue" ? money(t.revenue) : t.count}
                      </span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </div>

        {/* My listings (tab-scoped preview) */}
        <div className="bg-white rounded-2xl p-6 shadow border border-gray-100 mb-8">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 font-semibold">
              <Layers size={18} />{" "}
              <span className="capitalize">{activeType} Listings</span>
              <span className="text-gray-400 text-sm">
                ({itemsForTab.length})
              </span>
            </div>
            <a
              href="/manage-listing"
              className="text-sm text-indigo-600 hover:underline"
            >
              Manage all
            </a>
          </div>
          {itemsForTab.length === 0 ? (
            <div className="text-sm text-gray-400">
              No {activeType}s yet. Click “Manage Listings” to add one.
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {itemsForTab.slice(0, 8).map((it) => (
                <div
                  key={it.id}
                  className="group rounded-xl border border-gray-100 p-4 hover:shadow-md transition bg-white"
                >
                  <div className="flex items-center gap-3">
                    <SmartThumb item={it} />
                    <div className="min-w-0">
                      <div className="font-semibold truncate">
                        {it.title || "(Untitled)"}
                      </div>
                      <div className="mt-0.5 flex items-center gap-2">
                        <span className="text-[11px] px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 font-semibold">
                          {formatLKR(getItemPrice(it))}
                        </span>
                      </div>
                      <span
                        className={`mt-1 inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                          (it.status || "").toLowerCase() === "approved"
                            ? "bg-green-100 text-green-700"
                            : (it.status || "").toLowerCase() === "pending"
                            ? "bg-yellow-100 text-yellow-700"
                            : "bg-red-100 text-red-700"
                        }`}
                      >
                        {it.status || "pending"}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Sales (tab-scoped, filtered) */}
        <div className="bg-white rounded-2xl p-6 shadow border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <div className="font-semibold">
              Recent Sales (filtered) •{" "}
              <span className="capitalize">{activeType}</span>
            </div>
            <a
              href="/manage-listing"
              className="text-sm text-indigo-600 hover:underline"
            >
              View all
            </a>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-gray-500 text-xs">
                  <th className="text-left py-2">Item</th>
                  <th className="text-left py-2">Status</th>
                  <th className="text-right py-2">Amount</th>
                  <th className="text-right py-2">Your Share</th>
                  <th className="text-right py-2">Date</th>
                </tr>
              </thead>
              <tbody>
                {filtered.slice(0, 10).map((o) => (
                  <tr key={o.id} className="border-t">
                    <td className="py-2 pr-4">{o.title}</td>
                    <td className="py-2 capitalize">{o.statusNorm || "-"}</td>
                    <td className="py-2 text-right">{money(o.amount)}</td>
                    <td className="py-2 text-right font-semibold">
                      {money(o.share)}
                    </td>
                    <td className="py-2 text-right text-gray-500">
                      {o.ts?.toLocaleDateString?.() || "-"}
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td className="py-6 text-center text-gray-400" colSpan={5}>
                      No results for the selected filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------------------- helpers & tiny components -------------------- */
function KPI({ icon, title, value, sub }) {
  return (
    <div className="bg-white/90 border border-gray-100 rounded-2xl shadow p-5 hover:shadow-md transition">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-xl bg-gradient-to-br from-indigo-50 to-fuchsia-50 text-gray-700">
          {icon}
        </div>
        <div className="text-gray-500 text-xs">{title}</div>
      </div>
      <div className="text-2xl font-extrabold mt-2 tracking-tight">{value}</div>
      <div className="text-xs text-gray-400">{sub}</div>
    </div>
  );
}

function itemsForTypeCount(list, type) {
  return list.filter((x) => (x.type || "").toLowerCase() === type).length;
}
function getDateNDaysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - (n - 1)); // inclusive window
  return toISODate(d);
}
function toISODate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function weekKey(d) {
  const date = new Date(d);
  const target = new Date(date.valueOf());
  const dayNr = (date.getDay() + 6) % 7;
  target.setDate(target.getDate() - dayNr + 3);
  const firstThursday = new Date(target.getFullYear(), 0, 4);
  const diff = target - firstThursday;
  const week = 1 + Math.round(diff / (7 * 24 * 3600 * 1000));
  return `${target.getFullYear()}-W${String(week).padStart(2, "0")}`;
}
function monthKey(d) {
  const date = new Date(d);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
    2,
    "0"
  )}`;
}
function bucketize(rows, group, metric) {
  const map = new Map(); // key -> {label, value}
  const add = (key, label, val) => {
    if (!map.has(key)) map.set(key, { label, value: 0 });
    const prev = map.get(key);
    prev.value += val;
    map.set(key, prev);
  };
  rows.forEach((r) => {
    const ts = r.ts || new Date();
    if (group === "week") {
      const key = weekKey(ts);
      add(key, key, metric === "revenue" ? r.share : 1);
    } else if (group === "month") {
      const key = monthKey(ts);
      add(key, key, metric === "revenue" ? r.share : 1);
    } else {
      const key = ts.toISOString().slice(0, 10);
      const label = ts.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      });
      add(key, label, metric === "revenue" ? r.share : 1);
    }
  });
  return Array.from(map.entries())
    .sort((a, b) => (a[0] < b[0] ? -1 : 1))
    .map(([, v]) => v);
}
function resetFilters(
  setPreset,
  setDateFrom,
  setDateTo,
  setMetric,
  setGroup,
  setStatusSet,
  setItemIds,
  setItemSearch
) {
  setPreset("7d");
  setDateFrom(getDateNDaysAgo(7));
  setDateTo(toISODate(new Date()));
  setMetric("revenue");
  setGroup("day");
  setStatusSet(new Set(["paid", "completed", "approved", "success"]));
  setItemIds(new Set());
  setItemSearch("");
}
function exportCSV(rows) {
  const head = ["Date", "Status", "Item", "Type", "Amount", "Your Share"];
  const out = [head.join(",")];
  rows.forEach((r) => {
    const date = r.ts?.toISOString?.().slice(0, 19).replace("T", " ") || "";
    const line = [
      date,
      (r.statusNorm || "").toUpperCase(),
      `"${(r.title || "").replace(/"/g, '""')}"`,
      (r.type || "").toUpperCase(),
      String(r.amount || 0),
      String(r.share || 0),
    ].join(",");
    out.push(line);
  });
  const blob = new Blob([out.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `instructor-sales-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
